import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { t } from "i18next";

import type { ShareableIncidentDataV1Response } from "@thalamos/common";

import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { logActionToDatadog } from "~/tracing.js";

export type ArePolicePresentDialogProps = {
  onCloseOrOnTheirWay: () => void;
  onYes: () => void;
  onPreviouslyPresent: () => void;
  isDisabled: boolean;
  officerRequest: ShareableIncidentDataV1Response;
  version: number;
  arePolicePresentResponse?: "yes" | "no" | null;
};

export function ArePolicePresentDialog({
  onCloseOrOnTheirWay,
  onYes,
  onPreviouslyPresent,
  isDisabled,
  officerRequest,
  version,
  arePolicePresentResponse,
}: ArePolicePresentDialogProps) {
  const [arePresentResponse, setArePresentResponse] = useState<
    "yes" | "no" | null
  >(null);

  // Pre-fill response if provided (i.e. when coming back from later step)
  useEffect(() => {
    if (arePolicePresentResponse) {
      setArePresentResponse(arePolicePresentResponse);
    }
  }, [arePolicePresentResponse]);

  const [notPresentReason, setNotPresentReason] = useState<
    "previously-present" | "on-their-way" | null
  >(null);

  return (
    <PopupDialog
      open={true}
      onClose={() => {
        onCloseOrOnTheirWay();
      }}
    >
      <PopupDialogTitle
        titleText={
          version === 2 ? "Inform Police Presence" : "Are police present?"
        }
        subtitleText={version === 2 ? "Step 1 of 3" : undefined}
        closeDialog={() => {
          onCloseOrOnTheirWay();
        }}
      />
      <Box
        sx={{ display: "flex", p: 2, flexDirection: "column", gap: "1.5rem" }}
      >
        <Box>
          <FormControl>
            <FormLabel id="demo-radio-buttons-group-label">
              Are the police at your place of safety?
            </FormLabel>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              name="radio-buttons-group"
              value={arePresentResponse}
              onChange={(e) => {
                setArePresentResponse(e.target.value as "yes" | "no");
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>

        {arePresentResponse === "no" && (
          <FormControl fullWidth>
            <FormLabel id="demo-simple-select-label">
              Please select which of the following scenarios applies
            </FormLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={notPresentReason}
              onChange={(e) => {
                setNotPresentReason(
                  e.target.value as "previously-present" | "on-their-way",
                );
              }}
            >
              <MenuItem value={"previously-present"}>
                <Typography>
                  The police were <b>previously</b> at my place of safety
                </Typography>
              </MenuItem>
              <MenuItem value={"on-their-way"}>
                <Typography>
                  The police are <b>on their way</b> to my place of safety
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {notPresentReason === "on-their-way" && (
          <Banner
            bannerType={BannerList.WARNING}
            title="Further actions are only possible when the police have arrived at your place of safety. Please wait until they have arrived to continue"
          />
        )}

        <Box
          sx={{
            marginTop: "2em",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            label={t("buttonLabels.close")}
            testId="close-button"
            onClick={() => {
              onCloseOrOnTheirWay();
            }}
            variant="outlined"
          />
          <Button
            label={t("buttonLabels.confirm")}
            testId="confirm-button"
            type="submit"
            disabled={
              isDisabled ||
              arePresentResponse === null ||
              (arePresentResponse === "no" && notPresentReason === null)
            }
            onClick={() => {
              if (arePresentResponse === "yes") {
                onYes();
              } else if (notPresentReason === "previously-present") {
                onPreviouslyPresent();
              } else {
                onCloseOrOnTheirWay();
                logActionToDatadog(
                  "Police are on their way to the place of safety",
                  {
                    incidentId: officerRequest.incidentId,
                    officerName: officerRequest.officerDetails.name,
                    placeOfSafety: officerRequest.placesOfSafety.find(
                      (pos) => pos.isActive,
                    ),
                  },
                );
              }
            }}
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}
